const DEFAULT_MAX_LENGTH = 30
const DEFAULT_PADDING = 12

const buildOptions = (context, overrides) => {
  const datasetOptions = context?.dataset?.truncateLabelOptions

  return {
    maxLength: DEFAULT_MAX_LENGTH,
    padding: DEFAULT_PADDING,
    ellipsis: "...",
    ...datasetOptions,
    ...overrides,
  }
}

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value)

const resolveElementDimension = (element) => {
  if (!element) return null

  const model = element._model || element
  if (!model) return null

  // For text measurement, we always need the width (horizontal dimension)
  // For vertical bars: model.width is the bar width (constant for all segments)
  // For horizontal bars: model.width is the segment width (varies by value)
  // In both cases, model.width is what we need for text measurement

  // Priority: use model.width directly as it represents horizontal space
  if (isFiniteNumber(model.width) && model.width > 0) {
    return model.width
  }

  // Fallback: try to calculate width from positions
  // For vertical bars: base and x represent the bar boundaries
  // For horizontal bars: base and x represent the segment boundaries
  if (isFiniteNumber(model.base) && isFiniteNumber(model.x)) {
    const calculatedWidth = Math.abs(model.base - model.x)
    if (calculatedWidth > 0) {
      return calculatedWidth
    }
  }

  return null
}

// resolve input label (which can be array) to a value
const resolveLabelValue = (label, context) => {
  if (label === null || label === undefined) return label
  if (Array.isArray(label)) {
    return label[context?.dataIndex] ?? label[0]
  }
  return label
}

// coerce to string when a value exists; pass through null/undefined
const toStringLabel = (value) => {
  if (value === null || value === undefined) return value
  return typeof value === "string" ? value : String(value)
}

// derive canvas context and available width for text, considering padding
const getContextAndWidth = (context, padding) => {
  const chart = context?.chart
  const ctx = chart?.ctx
  const element = chart?.getDatasetMeta?.(context?.datasetIndex)?.data?.[
    context?.dataIndex
  ]
  let availableWidth = resolveElementDimension(element)

  if (isFiniteNumber(availableWidth)) {
    availableWidth = Math.max(availableWidth - padding, 0)
  }

  return { ctx, availableWidth }
}

// simple character-based truncation by max length
const truncateByChars = (text, maxLength, ellipsis) => {
  if (text.length <= maxLength) return text
  const sliceCount = Math.max(maxLength - ellipsis.length, 0)
  return `${text.slice(0, sliceCount)}${ellipsis}`
}

// width-aware truncation using canvas measurement
const truncateToAvailableWidth = (text, ctx, availableWidth, ellipsis) => {
  // Measure the full label first
  const fullLabelWidth = ctx.measureText(text).width
  if (fullLabelWidth <= availableWidth) return text

  // Binary search for optimal truncation point
  let left = 0
  let right = text.length
  let bestTruncated = ""

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const truncated = text.slice(0, mid)
    const truncatedWithEllipsis = `${truncated}${ellipsis}`
    const width = ctx.measureText(truncatedWithEllipsis).width

    if (width <= availableWidth) {
      bestTruncated = truncatedWithEllipsis
      left = mid + 1
    } else {
      right = mid - 1
    }
  }

  if (bestTruncated) return bestTruncated

  // Fall back to character-by-character if binary search didn't work
  let truncated = text
  while (truncated.length > 0) {
    const testText = `${truncated}${ellipsis}`
    if (ctx.measureText(testText).width <= availableWidth) return testText
    truncated = truncated.slice(0, -1)
  }

  return ellipsis
}

export const truncateLabel = (label, context, options = {}) => {
  const { maxLength, padding, ellipsis } = buildOptions(context, options)

  const resolvedLabel = resolveLabelValue(label, context)
  if (resolvedLabel === null || resolvedLabel === undefined) {
    return resolvedLabel
  }

  const stringLabel = toStringLabel(resolvedLabel)
  if (!stringLabel) return stringLabel

  const { ctx, availableWidth } = getContextAndWidth(context, padding)

  if (!ctx || !availableWidth) {
    return truncateByChars(stringLabel, maxLength, ellipsis)
  }

  return truncateToAvailableWidth(stringLabel, ctx, availableWidth, ellipsis)
}
